import { useEffect, useState } from "react";
import axios from "@lhd/libs/axios";
import {
  BUTTON_HEIGHT,
  DATE_TIME_FORMAT,
  FONT_SIZE,
  FONT_SIZE_VALUE,
  SERVICES,
  SUCCESSFUL_MESSAGE,
} from "@lhd/constants";
import moment from "moment";
import { notification } from "antd";
import { ERRORS_MESSAGE } from "@lhd/constants";
import { user } from "@lhd/features/users/selector";
import { get } from "lodash";
import { useSelector } from "react-redux";
import { getCompanyId } from "@lhd/utility";
import { Company, DISPLAY_SIZES } from "@lhd/types";
import { getDisplaySize } from "@lhd/components/DisplaySizeSelector/selector";

const useAdminCompanyConfig = () => {
  const [uploadingInfo, setUploadingInfo] = useState<{
    importTaxConfig: {
      uploading: boolean;
      percent: number;
    };
    accountTitles: {
      uploading: boolean;
      percent: number;
    };
  }>({
    importTaxConfig: {
      uploading: false,
      percent: 0,
    },
    accountTitles: {
      uploading: false,
      percent: 0,
    },
  });
  const auth: any = useSelector(user);
  const isAdmin = get(auth, "is_admin", false);
  const companies = get(auth, "companies", []);

  const companyId = getCompanyId();
  const companyOn = companies.find((com: Company) => {
    return com.id.toString() === companyId;
  });
  const isCompanyAdmin = !!companyOn?.is_admin;

  const displaySize = useSelector(getDisplaySize) as
    | keyof typeof FONT_SIZE
    | keyof typeof BUTTON_HEIGHT;
  const height = BUTTON_HEIGHT[displaySize] ?? BUTTON_HEIGHT.small;
  const size = FONT_SIZE_VALUE[displaySize ?? DISPLAY_SIZES.SMALL];

  const [timeUploaded, setTimeUploaded] = useState<{
    importTaxConfig: string;
    accountTitles: string;
  }>({
    importTaxConfig: "",
    accountTitles: "",
  });
  const notificationConfig = { ...notification };
  notificationConfig.config({
    duration: 5,
  });

  const handleGetTimeUploaded = (value?: string) =>
    value && moment(value).isValid()
      ? moment(value).format(DATE_TIME_FORMAT.DATE_TIME)
      : "";

  const fetchTimeUploaded = () => {
    axios({
      method: "get",
      url: SERVICES.LATEST_IMPORT,
    }).then((res) => {
      const importTaxConfig = res.data?.import_tax_config || "";
      const accountTitles = res.data?.account_titles || "";
      setTimeUploaded({
        importTaxConfig: handleGetTimeUploaded(importTaxConfig),
        accountTitles: handleGetTimeUploaded(accountTitles),
      });
    });
  };
  useEffect(fetchTimeUploaded, []);

  const handleUploadSeed = async (e: any) => {
    if (e.target.files.length) {
      setUploadingInfo({
        ...uploadingInfo,
        importTaxConfig: {
          uploading: true,
          percent: 0,
        },
      });
      const inputFile = e.target.files[0];
      const bodyFormData = new FormData();
      bodyFormData.append("file", inputFile);
      try {
        const { data }: { data: any } = await axios({
          method: "post",
          url: SERVICES.ADMIN_CONFIG,
          data: bodyFormData,
        });
        if (data?.status === "success") {
          setUploadingInfo({
            ...uploadingInfo,
            importTaxConfig: {
              uploading: true,
              percent: 100,
            },
          });
          notificationConfig["success"]({
            message: SUCCESSFUL_MESSAGE.UPLOAD_SUCCESSFUL,
          });
        } else {
          notificationConfig["error"]({
            message: ERRORS_MESSAGE.UPLOAD_FAILED,
          });
          setUploadingInfo({
            ...uploadingInfo,
            importTaxConfig: {
              uploading: false,
              percent: 0,
            },
          });
        }
      } catch (error) {
        notificationConfig["error"]({
          message: ERRORS_MESSAGE.UPLOAD_FAILED,
        });
      } finally {
        fetchTimeUploaded();
        setUploadingInfo({
          ...uploadingInfo,
          importTaxConfig: {
            uploading: false,
            percent: 0,
          },
        });
      }
    }
  };

  const handleUploadAccountTitles = async (e: any) => {
    if (e.target.files.length) {
      setUploadingInfo({
        ...uploadingInfo,
        accountTitles: {
          uploading: true,
          percent: 0,
        },
      });
      const inputFile = e.target.files[0];

      const bodyFormData = new FormData();
      bodyFormData.append("file", inputFile);
      try {
        const { data }: { data: any } = await axios({
          method: "post",
          url: SERVICES.ACCOUNT_TITLE_CONFIG,
          data: bodyFormData,
        });
        notificationConfig["success"]({
          message: SUCCESSFUL_MESSAGE.UPLOAD_SUCCESSFUL,
        });
        setUploadingInfo({
          ...uploadingInfo,
          accountTitles: {
            uploading: true,
            percent: 100,
          },
        });
      } catch (error) {
        notificationConfig["error"]({
          message: ERRORS_MESSAGE.UPLOAD_FAILED,
        });
      } finally {
        fetchTimeUploaded();
        setUploadingInfo({
          ...uploadingInfo,
          accountTitles: {
            uploading: false,
            percent: 0,
          },
        });
      }
    }
  };

  return {
    uploadingInfo,
    timeUploaded,
    handleUploadSeed,
    handleUploadAccountTitles,
    isCompanyAdmin,
    isAdmin,
    displaySize,
    height,
    size,
  };
};

export type Props = ReturnType<typeof useAdminCompanyConfig>;

export default useAdminCompanyConfig;
